/*
 * Whitelist of trusted script URLs that are allowed to execute.
 */
const trustedScriptUrls = [
  'https://omelette.net/omelette-sdk.umd.js',
  // 'https://genius.rhumba.ai/agent-chat-widget.js',
  // 'https://factory.rhumba.ai',
] as const;

function normalizeUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    // Only allow HTTPS
    if (parsed.protocol !== 'https:') {
      console.warn('Blocked non-HTTPS script:', url);
      return null;
    }
    // Return normalized URL without query params or fragments
    return `${parsed.protocol}//${parsed.hostname}${parsed.pathname}`;
  } catch {
    console.warn('Invalid URL format:', url);
    return null;
  }
}

/**
 * Checks if a script URL is in the trusted whitelist.
 */
function isScriptTrusted(src: string): boolean {
  const normalized = normalizeUrl(src);
  if (!normalized) return false;

  return trustedScriptUrls.some(trusted => {
    const trustedNormalized = normalizeUrl(trusted);
    return trustedNormalized === normalized;
  });
}

/**
 Executes trusted scripts within a given HTML node.
 Returns a Promise that resolves when all scripts are loaded.
 */
export function executeScriptsInNode(node: HTMLElement): Promise<void> {
  const scripts = node.querySelectorAll('script');

  if (scripts.length === 0) {
    return Promise.resolve();
  }

  const scriptPromises: Promise<void>[] = [];

  scripts.forEach((oldScript, index) => {
    const src = oldScript.getAttribute('src');

    // Block ALL inline scripts (no src attribute)
    if (!src) {
      console.warn(`[ExecuteScripts] Blocked inline script #${index} - no src attribute`);
      return;
    }

    // Block untrusted external scripts
    if (!isScriptTrusted(src)) {
      console.warn(`[ExecuteScripts] Blocked untrusted script #${index}:`, src);
      return;
    }

    // Create a promise for this script's load
    const scriptPromise = new Promise<void>((resolve, reject) => {
      // Only trusted scripts with src
      const newScript = document.createElement('script');

      // Only copy specific safe attributes
      const safeAttributes = ['src', 'async', 'defer', 'type', 'crossorigin', 'integrity'];
      safeAttributes.forEach(attrName => {
        const attrValue = oldScript.getAttribute(attrName);
        if (attrValue) {
          newScript.setAttribute(attrName, attrValue);
        }
      });

      // Add load event listener
      newScript.addEventListener('load', () => {
        resolve();
      });
      
      newScript.addEventListener('error', (e) => {
        console.error(`[ExecuteScripts] Script failed to load:`, src, e);
        reject(e);
      });

      // Replace old script with new one to trigger execution
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });

    scriptPromises.push(scriptPromise);
  });

  // Return a promise that resolves when all scripts are loaded
  return Promise.all(scriptPromises).then(() => {
    // All scripts loaded successfully
  }).catch((error) => {
    console.error('[ExecuteScripts] Some scripts failed to load:', error);
  });
}

